import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Buildings, Plus, Trash, PencilSimple, User, ArrowLeft, EnvelopeSimple, Phone } from "@phosphor-icons/react";
import PMRModal from "@/components/PMRModal";

export default function AdminPropertyManagers() {
  const [pms, setPms] = useState([]);
  const [editing, setEditing] = useState(null);   // full record OR null
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/property-managers");
      setPms(data || []);
    } catch (e) {
      toast.error("Failed to load property managers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (pm) => {
    if (!window.confirm(`Delete "${pm.name}"? Any landlords tagged to them will be un-tagged (they'll fall back to the default PM).`)) return;
    try {
      await api.delete(`/property-managers/${pm.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-5xl">
        <Link to="/admin/landlords" className="text-xs font-black uppercase tracking-widest text-slate-500 hover:text-[#00C4CC] inline-flex items-center gap-1">
          <ArrowLeft size={12}/> Back to Landlords
        </Link>
        <div className="mt-3 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-black text-4xl tracking-tight flex items-center gap-3">
              <Buildings weight="fill" size={30}/> Property Management Companies
            </h1>
            <p className="mt-1 text-slate-500 font-medium max-w-2xl">
              The <strong>companies</strong> that route work to you (not individual people). Each company has one or more <strong>Maintenance Coordinators</strong> inside it — the people who actually email you and your landlords. Each landlord gets tagged to a company; the coordinator's name + role show up in the email signature.
            </p>
          </div>
          <button onClick={() => setShowNew(true)} data-testid="add-pm-btn"
            className="tactile-btn h-11 px-5 bg-[#00C4CC] text-white font-black uppercase tracking-wider inline-flex items-center gap-2">
            <Plus weight="bold" size={16}/> New Property Manager
          </button>
        </div>

        {loading ? (
          <div className="mt-8 text-center text-slate-500 font-black uppercase text-sm tracking-widest">Loading…</div>
        ) : pms.length === 0 ? (
          <div className="mt-8 p-8 border-2 border-dashed border-slate-300 text-center" data-testid="pms-empty">
            <div className="font-display font-black text-xl">No property managers yet</div>
            <p className="mt-2 text-sm text-slate-500 max-w-md mx-auto">
              Add the property management companies that route work to your crew. Property Management Redefined would go here.
            </p>
            <button onClick={() => setShowNew(true)}
              className="mt-4 h-11 px-5 bg-[#0f172a] text-white font-black uppercase tracking-widest text-xs">
              Add your first PM
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {pms.map(pm => (
              <div key={pm.id} data-testid={`pm-card-${pm.id}`}
                className="rugged-card p-5 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-1" style={{ background: pm.brand_color || "#00C4CC" }}/>
                <div className="flex items-start gap-3">
                  {pm.logo_url ? (
                    <img src={pm.logo_url} alt={pm.name} className="h-12 w-12 object-contain bg-white border border-slate-200 p-1 flex-shrink-0"/>
                  ) : (
                    <div className="h-12 w-12 border-2 border-slate-200 bg-slate-50 flex items-center justify-center flex-shrink-0">
                      <Buildings weight="bold" size={22} className="text-slate-400"/>
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-black text-lg leading-tight truncate" data-testid={`pm-name-${pm.id}`}>{pm.name}</div>
                    {pm.contact_name && (
                      <div className="text-xs text-slate-600 mt-0.5 truncate">
                        {pm.contact_name}{pm.role_title ? ` · ${pm.role_title}` : ""}
                      </div>
                    )}
                    {(pm.contacts?.length || 0) > 0 && (
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        +{pm.contacts.length} more coordinator{pm.contacts.length === 1 ? "" : "s"}
                      </div>
                    )}
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest whitespace-nowrap">
                    <User size={11}/> {pm.landlord_count || 0} {(pm.landlord_count || 0) === 1 ? "LL" : "LLs"}
                  </span>
                </div>
                <div className="mt-3 space-y-1 text-xs text-slate-600">
                  {pm.email && <div className="flex items-center gap-1.5 truncate"><EnvelopeSimple size={12}/> {pm.email}</div>}
                  {pm.phone && <div className="flex items-center gap-1.5"><Phone size={12}/> {pm.phone}</div>}
                </div>
                <div className="mt-3 pt-3 border-t border-slate-200 flex items-center gap-3 flex-wrap">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Cap ${Number(pm.default_cost_cap || 0).toLocaleString()} · {pm.default_dispatch_window}
                  </span>
                  <div className="ml-auto flex gap-2">
                    <button onClick={() => setEditing(pm)} data-testid={`pm-edit-${pm.id}`}
                      className="h-8 px-2 border-2 border-[#0f172a] font-black uppercase tracking-widest text-[10px] hover:bg-slate-50 inline-flex items-center gap-1">
                      <PencilSimple size={10}/> Edit
                    </button>
                    <button onClick={() => del(pm)} data-testid={`pm-delete-${pm.id}`}
                      className="h-8 px-2 border-2 border-red-500 text-red-600 font-black uppercase tracking-widest text-[10px] hover:bg-red-50 inline-flex items-center gap-1">
                      <Trash size={10}/>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(showNew || editing) && (
        <PMRModal
          multi
          initial={editing}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }}
        />
      )}
    </AdminShell>
  );
}
